import React, { useLayoutEffect, useState } from 'react'
import cn from 'classnames'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import useStore from 'state/knovStore'
import { hasPanelInDirection } from 'lib/panelBoundaryFeedback'
import styles from './panel-edge-chevrons.module.scss'

// fixed_header, desktop: the panels beside the centred one (the strip's
// history) are signalled by chevrons just outside its frame, level with its
// header row, on whichever sides have panels. A thread's header carries a
// back arrow that slides to the panel on the left, so with a thread centred
// the left chevron steps aside: the two never show side by side.
const HEADER_ROW_CENTRE_PX = 28
const SETTLE_MS = 600
// What can move the centred frame: the panels (and their swiper track) and
// the sidebar beside them.
const MOVERS_SELECTOR =
    '.panel-container-comp, .swiper-wrapper, .swiper-slide, [class*="sidebar" i]'

type Place = { top: number; left: number; right: number } | null

function measureCentrePanel(): Place {
    const frame = document.querySelector<HTMLElement>('[data-panel-centre="true"]')
    if (!frame) return null
    const rect = frame.getBoundingClientRect()
    if (!rect.width) return null
    return { top: rect.top + HEADER_ROW_CENTRE_PX, left: rect.left, right: rect.right }
}

export default function PanelEdgeChevrons({
    onPrev,
    onNext,
}: {
    onPrev: () => void
    onNext: () => void
}) {
    const { canPrev, canNext, centerIndex, numPanels, panelsLength } = useStore(state => {
        const panels = state.panels.state || []
        const centre = panels[state.centerIndex]
        return {
            canPrev:
                hasPanelInDirection(panels, state.centerIndex, 'prev') &&
                !(centre?.filter?.questId && !centre?.newThread),
            canNext: hasPanelInDirection(panels, state.centerIndex, 'next'),
            centerIndex: state.centerIndex,
            numPanels: state.numPanels,
            panelsLength: panels.length,
        }
    })
    const [place, setPlace] = useState<Place>(null)

    // The centred frame moves while panels slide, windows rescale and the
    // sidebar opens or closes: follow it every frame while anything that can
    // move it is in motion (and for a moment after each change), so the
    // chevrons never sit at a stale spot, over the header's own controls.
    useLayoutEffect(() => {
        let frame = 0
        let until = 0
        const measure = () =>
            setPlace(prev => {
                const next = measureCentrePanel()
                if (
                    prev &&
                    next &&
                    prev.top === next.top &&
                    prev.left === next.left &&
                    prev.right === next.right
                )
                    return prev
                return next
            })
        const follow = () => {
            frame = 0
            measure()
            if (performance.now() < until) frame = requestAnimationFrame(follow)
        }
        const kick = () => {
            until = performance.now() + SETTLE_MS
            if (!frame) frame = requestAnimationFrame(follow)
        }
        // Only the movers' own transitions count (not the many hover effects
        // inside the panels), so the loop runs while the frame can actually
        // be moving and stops 600ms after.
        const onMotion = (event: Event) => {
            const target = event.target as Element | null
            if (target?.matches?.(MOVERS_SELECTOR)) kick()
        }
        measure()
        kick()
        window.addEventListener('resize', kick)
        document.addEventListener('transitionrun', onMotion, true)
        document.addEventListener('transitionend', onMotion, true)
        return () => {
            cancelAnimationFrame(frame)
            window.removeEventListener('resize', kick)
            document.removeEventListener('transitionrun', onMotion, true)
            document.removeEventListener('transitionend', onMotion, true)
        }
    }, [centerIndex, numPanels, panelsLength])

    if (!place) return null

    return (
        <>
            <button
                type="button"
                aria-label="Panel to the left"
                data-testid="panel-edge-prev"
                className={cn(styles.chevron, styles.prev, !canPrev && styles.hidden)}
                style={{ top: place.top, left: place.left }}
                onClick={onPrev}
                tabIndex={canPrev ? 0 : -1}
            >
                <ChevronLeft size={22} strokeWidth={2.2} />
            </button>
            <button
                type="button"
                aria-label="Panel to the right"
                data-testid="panel-edge-next"
                className={cn(styles.chevron, styles.next, !canNext && styles.hidden)}
                style={{ top: place.top, left: place.right }}
                onClick={onNext}
                tabIndex={canNext ? 0 : -1}
            >
                <ChevronRight size={22} strokeWidth={2.2} />
            </button>
        </>
    )
}
